// Les photos finissent imprimées dans un livre : on privilégie la qualité.
// 3000 px sur le grand côté permettent une impression pleine page (~25 cm)
// à 300 dpi, et la qualité 0,92 évite les artefacts visibles sur papier.
const MAX_DIMENSION = 3000;
const QUALITY = 0.92;

const loadImageUrl = (url) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Image illisible"));
        image.src = url;
    });
};

const loadImage = async (file) => {
    const url = URL.createObjectURL(file);
    try {
        return await loadImageUrl(url);
    } finally {
        URL.revokeObjectURL(url);
    }
};

/**
 * Indique si une image est en portrait. Une image illisible ou absente est
 * considérée comme paysage, qui est le format d'affichage par défaut.
 *
 * @param {String} url
 * @returns {Promise<Boolean>}
 */
export async function isPortraitImage(url) {
    if (!url) return false;
    try {
        const image = await loadImageUrl(url);
        return image.naturalHeight > image.naturalWidth;
    } catch (e) {
        return false;
    }
}

const toBlob = (canvas, quality) => {
    return new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality);
    });
};

const jpegName = (name) => {
    return name.replace(/\.[^.]+$/, '') + '.jpg';
};

/**
 * Réduit une photo avant envoi, sans descendre sous une définition
 * exploitable pour l'impression. Renvoie le fichier d'origine si la
 * compression n'apporte rien ou si l'image n'a pas pu être décodée.
 *
 * @param {File} file
 * @param {{ maxDimension?: number, quality?: number }} options
 * @returns {Promise<File>}
 */
export async function compressImage(file, options = {}) {
    if (!file.type.startsWith('image/')) return file;

    const maxDimension = options.maxDimension ?? MAX_DIMENSION;
    const quality = options.quality ?? QUALITY;

    let image;
    try {
        image = await loadImage(file);
    } catch (e) {
        // Format non décodable par le navigateur (HEIC sur desktop par
        // exemple) : on laisse le back se débrouiller avec l'original.
        return file;
    }

    // On ne ré-échantillonne jamais vers le haut : ça n'ajoute aucun détail
    // et ferait grossir le fichier pour rien.
    const ratio = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.naturalWidth * ratio);
    canvas.height = Math.round(image.naturalHeight * ratio);

    const context = canvas.getContext('2d');
    // Le JPEG n'a pas de transparence : sans ce fond, les zones
    // transparentes d'un PNG ressortiraient en noir.
    context.fillStyle = '#FFF';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await toBlob(canvas, quality);
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], jpegName(file.name), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
    });
}

export default compressImage;
